import React from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { LabStatusCountMap } from '@/src/types/LabDashboard';
import { isInMonth, isSameDay } from './calendar.utils';
import { PRIMARY_STATUSES, SECONDARY_STATUSES, STATUS_LABEL_KEYS } from './labStatus.constants';
import styles from './LabCalendar.module.scss';
import { getLanguageCodeSafe } from '@/src/utils/locale.utils';

/**
 * Per-cell content data passed from CalendarGrid.
 * Groups all content-related values into a single object to keep the prop list readable.
 */
export interface DayCellData {
  /** Status counts for this date (past/today cells only) */
  dailySummary?: LabStatusCountMap;
  /** Predicted lab count for this date (future cells only) */
  predictedLabs?: number;
  /** External content rendered by the parent's renderDayContent callback */
  externalContent?: React.ReactNode;
}

export interface DayCellProps {
  date: Date;
  displayedMonth: Date;
  today: Date;
  data: DayCellData;
  onDayClick?: (date: Date) => void;
  tabIndex?: number;
  onFocus?: () => void;
}

/**
 * Filters a LabStatusCountMap to only include the given statuses, in order.
 * Returns entries as [statusKey, count] pairs, or empty array if none match.
 */
const getDisplayedEntries = (statusMap: LabStatusCountMap, statuses: string[]): [string, number][] => {
  return statuses.filter((status) => status in statusMap).map((status) => [status, statusMap[status]]);
};

/**
 * Renders a single cell within the calendar grid representing one day.
 * Computes its own display state (isCurrentMonth, isToday) from the provided
 * displayedMonth and today references, and renders content from the data prop.
 */
const DayCell = React.forwardRef<HTMLTableCellElement, DayCellProps>(
  ({ date, displayedMonth, today, data, onDayClick, tabIndex, onFocus }, ref) => {
    const { t, i18n } = useTranslation();
    const isCurrentMonth = isInMonth(date, displayedMonth.getFullYear(), displayedMonth.getMonth());
    const isToday = isSameDay(date, today);
    const dayNumber = date.getDate();

    // Non-current-month cells render as blank
    if (!isCurrentMonth) {
      return (
        <td ref={ref} className={styles.dayCellBlank} role="gridcell" tabIndex={tabIndex} onFocus={onFocus}>
          <div className={styles.dayInner} />
        </td>
      );
    }

    let cellClassName = styles.dayCell;
    if (isToday) {
      cellClassName = styles.dayCellToday;
    }

    // Build content from the data prop
    const { dailySummary, predictedLabs, externalContent } = data;
    let content: React.ReactNode | null = externalContent ?? null;

    if (dailySummary) {
      const primaryEntries = getDisplayedEntries(dailySummary, PRIMARY_STATUSES);
      const secondaryEntries = getDisplayedEntries(dailySummary, SECONDARY_STATUSES);
      const renderEntry = ([status, count]: [string, number]) => (
        <div key={status} className={styles.daySummaryEntry}>
          <span className={styles.daySummaryLabel}>{`${t(STATUS_LABEL_KEYS[status])}:`}</span>
          <span className={styles.daySummaryCount}>{count}</span>
        </div>
      );
      if (primaryEntries.length > 0 || secondaryEntries.length > 0) {
        content = (
          <>
            {externalContent}
            <div className={styles.daySummary}>
              {primaryEntries.map(renderEntry)}
              {primaryEntries.length > 0 && secondaryEntries.length > 0 && (
                <span className={styles.daySummarySeparator} aria-hidden="true" data-testid="day-summary-separator" />
              )}
              {secondaryEntries.map(renderEntry)}
            </div>
          </>
        );
      }
    } else if (predictedLabs != null && predictedLabs > 0) {
      content = (
        <>
          {externalContent}
          <div className={styles.daySummary}>
            <div className={styles.daySummaryEntry}>
              {t(i18nKeys.labDashboard.calendar.predictedLabs, { count: predictedLabs })}
            </div>
          </div>
        </>
      );
    }

    const isClickable = !!onDayClick;

    const handleClick = isClickable
      ? () => {
          onDayClick(date);
        }
      : undefined;

    const handleKeyDown = isClickable
      ? (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onDayClick(date);
          }
        }
      : undefined;

    const formattedDate = date.toLocaleDateString(getLanguageCodeSafe(i18n.language), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    return (
      <td
        ref={ref}
        className={cellClassName}
        role="gridcell"
        aria-current={isToday ? 'date' : undefined}
        tabIndex={tabIndex}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.dayInner}>
          <div className={styles.dayInnerContent}>
            {isClickable ? (
              <button
                type="button"
                className={styles.dayButton}
                onClick={handleClick}
                aria-label={t(i18nKeys.labDashboard.calendar.dayAriaLabel, { date: formattedDate })}
                tabIndex={-1}
                data-testid={`day-cell__${dayNumber}-btn`}
              >
                <span className={styles.dayNumber}>{dayNumber}</span>
                {content && <div className={styles.dayContent}>{content}</div>}
              </button>
            ) : (
              <>
                <span className={styles.dayNumber}>{dayNumber}</span>
                {content && <div className={styles.dayContent}>{content}</div>}
              </>
            )}
          </div>
        </div>
      </td>
    );
  }
);

DayCell.displayName = 'DayCell';

export default DayCell;
